'use client'

import { useRef, useState, useTransition } from 'react'
import { cn, Input, Label, TextArea, TextField, toast } from '@heroui/react'
import { TurnstileWidget } from '@/components/forms/turnstile-widget'
import { Button } from '@/components/ui/button'
import { CompatSelect as Select, ListBoxItem } from '@/components/ui/compat-select'
import { Checkbox } from '@/components/ui/checkbox'
import { Icon } from '@/components/ui/icon'
import { pushFormSubmit } from '@/lib/analytics/funnel-events'
import { submitFleetLead } from '@/lib/services/actions/fleet-lead'

interface FleetQuoteFormProps {
  locationId: string
}

// Mirrors the live catalog. Window tint is deliberately absent: the shop no longer
// sells it, so offering it here would generate leads we cannot fulfil.
const SERVICE_OPTIONS = [
  'Colour Change Wrap',
  'Commercial Graphics',
  'Commercial Decals',
  'Paint Protection Film',
  'Fleet Rollouts',
  'Multiple / Not sure',
]

const TIMELINE_OPTIONS = [
  'As soon as possible',
  'Within 1 month',
  '1-3 months',
  '3+ months',
  'Just exploring',
]

const CONTACT_METHODS: { value: 'sms' | 'call'; label: string; icon: 'chat-round-dots-linear' | 'phone-call' }[] = [
  { value: 'sms', label: 'Text / SMS', icon: 'chat-round-dots-linear' },
  { value: 'call', label: 'Phone call', icon: 'phone-call' },
]

export function FleetQuoteForm({ locationId }: FleetQuoteFormProps) {
  const [isPending, startTransition] = useTransition()
  const [submitted, setSubmitted] = useState(false)
  // Synchronous reentrancy lock. `isPending` flips on the next render, so a fast
  // double click can fire handleSubmit twice before the button disables; this ref
  // closes that window so only one lead is ever in flight.
  const inFlightRef = useRef(false)

  const [companyName, setCompanyName] = useState('')
  const [contactName, setContactName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [contactMethod, setContactMethod] = useState<'sms' | 'call'>('sms')
  const [fleetSize, setFleetSize] = useState('')
  const [vehicleTypes, setVehicleTypes] = useState('')
  const [serviceInterests, setServiceInterests] = useState<string[]>([])
  const [timeline, setTimeline] = useState('')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  // Bumped after a rejected submit: the token is single-use, so the widget has
  // to draw a fresh one before the customer can try again.
  const [turnstileNonce, setTurnstileNonce] = useState(0)

  const toggleService = (service: string, isSelected: boolean) => {
    setServiceInterests((prev) =>
      isSelected ? [...prev, service] : prev.filter((s) => s !== service)
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Synchronous reentrancy guard: see inFlightRef.
    if (inFlightRef.current) return
    inFlightRef.current = true

    startTransition(async () => {
      try {
        const result = await submitFleetLead({
          companyName,
          contactName,
          email,
          phone,
          contactMethod,
          fleetSize: fleetSize ? Number(fleetSize) : undefined,
          vehicleTypes: vehicleTypes || undefined,
          serviceInterest: serviceInterests.length ? serviceInterests.join(', ') : undefined,
          timeline: timeline || undefined,
          message: message || undefined,
          shopLocationId: locationId,
          website,
          turnstileToken: turnstileToken ?? undefined,
        })

        if (result.success) {
          if (result.data?.id && result.data.id !== 'ignored')
            pushFormSubmit('fleet-quote', result.data.id, { email, phone })
          toast.success('Request sent', {
            description: "We'll be in touch within one business day.",
          })
          setSubmitted(true)
          setCompanyName('')
          setContactName('')
          setEmail('')
          setPhone('')
          setContactMethod('sms')
          setFleetSize('')
          setVehicleTypes('')
          setServiceInterests([])
          setTimeline('')
          setMessage('')
          setWebsite('')
        } else {
          setTurnstileNonce((n) => n + 1)
          toast.danger('Something went wrong', {
            description: result.error || 'Please try again.',
          })
        }
      } finally {
        inFlightRef.current = false
      }
    })
  }

  if (submitted) {
    return (
      <div
        className="bg-surface border border-border rounded-2xl p-8 text-center"
        role="status"
        aria-live="polite"
      >
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-success">
          <Icon icon="check-circle" className="text-2xl" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Request received</h2>
        <p className="text-sm text-muted mb-5 max-w-sm mx-auto">
          Thanks for reaching out. Our fleet team will review your details and get back to you
          within one business day.
        </p>
        <Button variant="outline" onPress={() => setSubmitted(false)}>
          Submit another request
        </Button>
      </div>
    )
  }

  return (
    <div className="bg-surface border border-border rounded-2xl p-6 sm:p-8">
      <form onSubmit={handleSubmit} className="space-y-4" aria-label="Fleet quote request form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField value={companyName} onChange={setCompanyName} isRequired>
            <Label>Company name</Label>
            <Input variant="outline" />
          </TextField>
          <TextField value={contactName} onChange={setContactName} isRequired>
            <Label>Your name</Label>
            <Input variant="outline" />
          </TextField>
        </div>

        <TextField value={email} onChange={setEmail} isRequired>
          <Label>Email</Label>
          <Input type="email" variant="outline" />
        </TextField>

        {/* Phone + preferred-contact toggle */}
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <TextField value={phone} onChange={setPhone} isRequired className="flex-1">
              <Label>Phone</Label>
              <Input type="tel" inputMode="tel" variant="outline" />
            </TextField>
            <div
              role="radiogroup"
              aria-label="Preferred contact method"
              className="relative inline-grid shrink-0 grid-cols-2 rounded-full border border-border bg-background p-1"
            >
              {/* Sliding thumb that animates beneath the active option */}
              <span
                aria-hidden="true"
                className={cn(
                  'pointer-events-none absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-accent shadow-sm transition-transform duration-200 ease-out',
                  contactMethod === 'call' && 'translate-x-full'
                )}
              />
              {CONTACT_METHODS.map((m) => {
                const active = contactMethod === m.value
                return (
                  <button
                    key={m.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    aria-label={m.label}
                    onClick={() => setContactMethod(m.value)}
                    className={cn(
                      'relative z-10 flex items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
                      active ? 'text-accent-foreground' : 'text-muted hover:text-foreground'
                    )}
                  >
                    <Icon icon={m.icon} className="text-base" />
                    <span className="whitespace-nowrap">{m.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
          <p className="text-xs text-muted">How would you like us to reach you?</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField value={fleetSize} onChange={setFleetSize}>
            <Label>Number of vehicles</Label>
            <Input type="number" inputMode="numeric" min={1} variant="outline" />
          </TextField>
          <div className="space-y-1.5">
            <span className="block text-sm font-medium text-foreground">Timeline</span>
            <Select
              aria-label="Project timeline"
              placeholder="Select a timeline"
              selectedKeys={timeline ? [timeline] : []}
              onSelectionChange={(keys) => {
                const val = Array.from(keys)[0] as string
                setTimeline(val || '')
              }}
              variant="outline"
            >
              {TIMELINE_OPTIONS.map((t) => (
                <ListBoxItem key={t}>{t}</ListBoxItem>
              ))}
            </Select>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="block text-sm font-medium text-foreground mb-1">
            Service of interest{' '}
            <span className="font-normal text-muted">(select all that apply)</span>
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
            {SERVICE_OPTIONS.map((s) => (
              <Checkbox
                key={s}
                isSelected={serviceInterests.includes(s)}
                onChange={(isSelected) => toggleService(s, isSelected)}
              >
                {s}
              </Checkbox>
            ))}
          </div>
        </fieldset>

        <TextField value={vehicleTypes} onChange={setVehicleTypes}>
          <Label>Vehicle types</Label>
          <Input variant="outline" placeholder="e.g. cargo vans, pickups, box trucks" />
        </TextField>

        <TextField value={message} onChange={setMessage}>
          <Label>Project details</Label>
          <TextArea
            variant="outline"
            rows={3}
            placeholder="Tell us about your fleet and what you're looking for..."
          />
        </TextField>

        {/* Honeypot: hidden from people, filled by bots. */}
        <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
          <label>
            Website
            <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </label>
        </div>

        <TurnstileWidget onToken={setTurnstileToken} resetSignal={turnstileNonce} />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          isLoading={isPending}
          isDisabled={isPending}
        >
          {isPending ? 'Sending...' : 'Request Fleet Quote'}
        </Button>
      </form>
    </div>
  )
}
